import { resolveCohortIds } from '@/components/cohorts/cohort-multi-select';
import { useMyLearningPaths } from '@/hooks/queries/learning-paths';
import { useMyCohorts } from '@/hooks/queries/cohorts';
import PromptService from '@/services/prompt-service';
import { Icon } from '@iconify/react';
import { Card, Selection, Tab, Tabs, cn } from '@nextui-org/react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { PromptData, StandardResponse } from 'Src/types/agent';

import { getCategoryStyle } from '@/utils/colors';
import { filterItemsByCohortIds, learningPathsToScenarios } from '@/utils/cohort-filter';
import { DEFAULT_DASHBOARD_TABS } from '../../../constants/default-tabs';
import { useTenantConfig } from '../../../hooks/queries/tenant-config';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import { useHubTab } from '@/hooks/useHubTab';
import { getTagGradient, normalizeCategory } from '../../../utils/colors';
import DisplayScenarios from './display-scenarios';
/**
 * Get the appropriate icon for a given tag (consistent with mobile swipable-tabs)
 */
const getTabIcon = (tag: string) => {
  const normalized = normalizeCategory(tag);
  switch (normalized) {
    case 'Counselor':
      return 'lucide:sparkles';
    case 'Action':
      return 'lucide:dumbbell';
    case 'Coach':
      return 'lucide:target';
    default:
      return 'lucide:sparkles';
  }
};

/**
 * Get the description for a given tag
 */
const getTabDescription = (tag: string): string => {
  const normalized = normalizeCategory(tag);
  switch (normalized) {
    case 'Counselor':
      return 'Discover and activate your core potential';
    case 'Action':
      return 'Practice leadership actions with instant feedback';
    case 'Coach':
      return 'Prepare for upcoming challenges in your workday';
    default:
      return 'Explore leadership tools and resources';
  }
};

const TabsSection = ({
  selectedCohortKeys,
}: {
  selectedCohortKeys: Selection;
}) => {
  const { tenantId } = useActiveTenant();
  const { data: tenantConfig } = useTenantConfig(tenantId!);

  // Get configured tabs or fall back to defaults
  const configuredTabs = useMemo(() => {
    const tabs = tenantConfig?.dashboard_config?.tabs;
    if (!tabs || tabs.length === 0) {
      return DEFAULT_DASHBOARD_TABS.map((tab) => ({
        ...tab,
        id: `default-${tab.priority}`,
      }));
    }
    return tabs.sort((a, b) => a.priority - b.priority);
  }, [tenantConfig]);

  // Defaults to the first active tab's tag
  const activeTags = useMemo(() => {
    const active = configuredTabs.filter((tab) => tab.is_active);
    return (active.length > 0 ? active : configuredTabs.slice(0, 1)).map(
      (tab) => tab.tag
    );
  }, [configuredTabs]);

  const [tag, setTag] = useHubTab(activeTags);

  const { data: cohorts } = useMyCohorts();

  const { data: prompts, isLoading: isLoadingPrompts } = useQuery<
    StandardResponse<PromptData>
  >({
    queryKey: [`agent-${tag}`, { is_active: true, tag }],
    queryFn: async (): Promise<StandardResponse<PromptData>> => {
      const response = await PromptService.getPrompts({
        filters: {
          page: 1,
          is_active: true,
          tag,
        },
      });
      return response as StandardResponse<PromptData>;
    },
  });

  const { data: learningPaths, isLoading: isLoadingLearningPaths } =
    useMyLearningPaths({
      tag,
    });

  // Get the gradient for the current tab
  const cursorGradient = getTagGradient(tag);
  const currentDescription = getTabDescription(tag);

  const selectedCohortIds = useMemo(
    () => resolveCohortIds(selectedCohortKeys, cohorts?.results ?? []),
    [selectedCohortKeys, cohorts?.results]
  );

  const allCohortIds = useMemo(
    () => cohorts?.results?.map((cohort) => cohort.id) ?? [],
    [cohorts?.results]
  );

  const data = useMemo(() => {
    if (import.meta.env.VITE_LEARNING_PATH_ENABLED === 'true') {
      return learningPathsToScenarios(
        learningPaths?.results ?? [],
        selectedCohortIds,
        allCohortIds
      );
    }

    const promptItems = (prompts?.results || []).map((prompt) => ({
      id: prompt.id,
      name: prompt.name,
      description: prompt.system_prompt || '',
      cohort_ids: prompt.cohort_ids ?? [],
    }));

    return filterItemsByCohortIds(
      promptItems,
      selectedCohortIds,
      allCohortIds
    );
  }, [
    learningPaths?.results,
    prompts?.results,
    selectedCohortIds,
    allCohortIds,
  ]);

  const isLoading = useMemo(() => {
    if (import.meta.env.VITE_LEARNING_PATH_ENABLED === 'true') {
      return isLoadingLearningPaths;
    }
    return isLoadingPrompts;
  }, [isLoadingPrompts, isLoadingLearningPaths]);

  return (
    <Card
      className="p-6 min-h-[71vh] bg-[#f8f9fa] rounded-[20px] border border-black/[0.06] shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)]"
      aria-label="Dashboard content"
      shadow="none"
    >
      {/* Inject dynamic cursor style for the selected tab */}
      <style>
        {`
          .dashboard-tabs [data-slot="cursor"] {
            background: ${cursorGradient} !important;
            border-radius: 0.75rem !important;
          }
        `}
      </style>
      <div className="flex w-full flex-col">
        <Tabs
          aria-label="Dashboard category tabs"
          variant="light"
          selectedKey={tag}
          onSelectionChange={(key) => setTag(String(key))}
          className="dashboard-tabs"
          classNames={{
            tabList:
              'bg-white p-1.5 rounded-2xl gap-0 w-fit shadow-[0_4px_16px_rgba(0,0,0,0.08)]',
            cursor: 'rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.15)]',
            tab: 'h-11 px-4 data-[hover=true]:opacity-100',
            tabContent:
              'group-data-[selected=true]:text-white text-[#71717a] font-normal text-base',
          }}
        >
          {configuredTabs
            .filter((tab) => tab.is_active)
            .map((tab) => {
              const isSelected = tag === tab.tag;
              const iconName = getTabIcon(tab.tag);
              return (
                <Tab
                  key={tab.tag}
                  title={
                    <div className="flex items-center gap-2">
                      <Icon
                        icon={iconName}
                        className={`w-4 h-4 transition-colors ${
                          isSelected ? 'text-white' : 'text-gray-500'
                        }`}
                        aria-hidden="true"
                      />
                      <span>{tab.label}</span>
                    </div>
                  }
                  aria-label={tab.label}
                >
                  <div className="mt-2">
                    {/* Tab Description - only show if there are no tabs configured */}
                    {tenantConfig?.dashboard_config?.tabs?.length === 0 && (
                      <motion.p
                        key={tag}
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        // className="text-[#5B8FEA] text-sm font-medium mb-4"
                        className={cn('text-sm font-medium mb-4')}
                        style={getCategoryStyle(tag)}
                      >
                        {currentDescription}
                      </motion.p>
                    )}
                    <DisplayScenarios
                      tasks={data}
                      isLoading={isLoading}
                      tag={tag}
                      selectedCohortIds={selectedCohortIds}
                    />
                  </div>
                </Tab>
              );
            })}
        </Tabs>
      </div>
    </Card>
  );
};

export default TabsSection;
